import { useCallback, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export type BetaTermsStatus = 'loading' | 'ok' | 'required';

/**
 * Prüft, ob der eingeloggte User den aktuellen Beta-/Haftungs-Hinweis
 * (Spalte profiles.beta_terms_accepted_at) bereits akzeptiert hat.
 *
 * NULL → status='required' → BetaTermsModal blockiert die App.
 * timestamptz → status='ok' → User kann normal weiterarbeiten.
 *
 * `accept()` schreibt now() in die Spalte und kippt den Status auf 'ok'.
 */
export function useBetaTermsConsent(user: User | null): {
  status: BetaTermsStatus;
  accept: () => Promise<void>;
} {
  const [status, setStatus] = useState<BetaTermsStatus>('loading');

  const check = useCallback(async (userId: string) => {
    if (!supabase) {
      // Ohne Supabase (z. B. Mock-Mode lokal) blocken wir nicht.
      setStatus('ok');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('beta_terms_accepted_at')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        // Spalte fehlt eventuell (Migration noch nicht eingespielt) — Fail-Open,
        // damit wir die App nicht permanent blockieren wenn die DB nachzieht.
        // Der Server-Endpoint wird die Aktion ggf. ohnehin erlauben.
        console.warn('[useBetaTermsConsent] check failed, fail-open', error);
        setStatus('ok');
        return;
      }
      setStatus(data?.beta_terms_accepted_at ? 'ok' : 'required');
    } catch (e) {
      console.warn('[useBetaTermsConsent] check threw, fail-open', e);
      setStatus('ok');
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setStatus('loading');
      return;
    }
    void check(user.id);
  }, [user, check]);

  const accept = useCallback(async () => {
    if (!supabase || !user) return;
    const { error } = await supabase
      .from('profiles')
      .update({ beta_terms_accepted_at: new Date().toISOString() })
      .eq('id', user.id);
    if (error) {
      console.error('[useBetaTermsConsent] accept failed', error);
      throw error;
    }
    setStatus('ok');
  }, [user]);

  return { status, accept };
}
